import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TabsContent } from "@/components/ui/tabs";
import { EmptyState, inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

export function PayrollTab({ payslips, employees, onRefresh }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const runPayroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/employer/payroll/run", { month });
      toast.success(`${data.payslips_generated} payslip(s) generated for ${data.month}`);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TabsContent value="payroll">
      <div className="bg-white border border-border/60 rounded-xl p-6 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <Label>Payroll month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 w-48" data-testid="payroll-month-input" />
        </div>
        <Button onClick={runPayroll} disabled={busy || employees.length === 0} className="rounded-full bg-[#0B1B3D]" data-testid="run-payroll-btn">
          {busy ? "Processing…" : "Run Payroll"}
        </Button>
        <p className="text-xs text-muted-foreground">India payroll module — Basic/HRA/PF/PT splits configurable by admin.</p>
      </div>
      {payslips.length === 0 ? (
        <EmptyState title="No payslips yet" description="Run payroll for a month to generate payslips for active employees." testId="payroll-empty" />
      ) : (
        <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="payslips-table">
            <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-5 py-3.5 font-semibold">Employee</th>
                <th className="px-5 py-3.5 font-semibold">Month</th>
                <th className="px-5 py-3.5 font-semibold">Gross</th>
                <th className="px-5 py-3.5 font-semibold">Deductions</th>
                <th className="px-5 py-3.5 font-semibold">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {payslips.map((p) => (
                <tr key={p.id} className="border-b border-border/40">
                  <td className="px-5 py-4 font-semibold">{p.name}<span className="text-xs text-muted-foreground ml-2">{p.emp_code}</span></td>
                  <td className="px-5 py-4 text-xs">{p.month}</td>
                  <td className="px-5 py-4">{inr(p.gross)}</td>
                  <td className="px-5 py-4 text-red-600">-{inr(p.total_deductions)}</td>
                  <td className="px-5 py-4 font-bold text-emerald-700">{inr(p.net_pay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TabsContent>
  );
}
